import { useEffect, useRef, useState, useCallback } from 'react';

const useWebSocket = (url) => {
  const [lastMessage, setLastMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const ws = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Reduced - fall back to polling faster
  const reconnectDelay = useRef(1000);

  const connect = useCallback(() => {
    try {
      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token available, skipping WebSocket connection');
        return;
      }

      // Close existing connection if any
      if (ws.current && ws.current.readyState !== WebSocket.CLOSED) {
        ws.current.close();
      }

      // Add token as query parameter for WebSocket auth
      const wsUrlWithToken = `${url}?token=${encodeURIComponent(token)}`;
      ws.current = new WebSocket(wsUrlWithToken);

      ws.current.onopen = () => {
        console.log('WebSocket connected to', url);
        setIsConnected(true);
        reconnectAttempts.current = 0;
        reconnectDelay.current = 1000;
      };

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // Skip ping messages
          if (message.type !== 'ping') {
            setLastMessage(message);
          }
          // Handle auth errors
          if (message.type === 'error' && message.message === 'Authentication required') {
            console.log('WebSocket authentication failed');
            ws.current.close();
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      ws.current.onclose = (event) => {
        setIsConnected(false);
        console.log('WebSocket disconnected, code:', event.code);

        // Attempt to reconnect with limited retries
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          console.log(
            `Reconnecting (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})...`
          );
          setTimeout(connect, reconnectDelay.current);
          reconnectDelay.current = Math.min(reconnectDelay.current * 2, 5000);
        } else {
          console.log('WebSocket reconnect failed, falling back to polling');
        }
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
      setIsConnected(false);
    }
  }, [url]);

  useEffect(() => {
    connect();

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect]);

  return { lastMessage, isConnected };
};

export default useWebSocket;
